import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckIcon } from "@heroicons/react/24/outline";
import { useInView } from "../hooks/useInView";
import { supabase } from "../lib/supabase";

const initialIncluded = [
  "Unlimited books and borrowers",
  "All reports, printable or in Excel",
  "Barcode scanner support",
  "Automatic book details from barcodes",
  "Automatic backups",
  "12 months of updates and support",
  "Email support included",
];

const renewalIncluded = [
  "Another 12 months of updates",
  "Bug fixes and OS compatibility patches",
  "New features as they ship (~2 per year)",
  "Continued email support",
];

type PricingFaq = {
  q: string;
  a: string;
  faqId?: string;
};

const faqs: PricingFaq[] = [
  {
    q: "What happens if I don't renew?",
    a: "Nothing breaks. The version of Bookburrow you already have keeps working forever \u2014 no features disabled, no data locked, no read-only mode. You simply stop receiving new updates and support until you choose to renew. You can skip a year, skip five years, and come back whenever.",
    faqId: "renewal",
  },
  {
    q: "Can I skip a year and renew later?",
    a: "Yes. When you're ready to catch up on updates, pay \u00a349 and you're back on the current version with a fresh 12 months of support. You don't pay for the years you skipped.",
  },
  {
    q: "What exactly does the \u00a349 renewal cover?",
    a: "Ongoing bug fixes, OS compatibility patches (so Bookburrow keeps running as Windows and Linux release new versions), new features as they ship, and email support for your questions. Roughly two feature updates per year once the product matures.",
  },
  {
    q: "Is there really no per-pupil fee?",
    a: "None. Whether you have 100 or 2,000 pupils, the price is the same. We know school budgets are stretched \u2014 your library system shouldn't be priced by headcount.",
    faqId: "one-time-price",
  },
  {
    q: "What happens after the free trial?",
    a: "Your data stays put. If you choose not to buy, Bookburrow becomes read-only \u2014 you can still search and view your catalogue, but lending is disabled until you purchase a licence.",
    faqId: "free-trial",
  },
  {
    q: "Do we need internet to use Bookburrow?",
    a: "Not for day-to-day lending and returns. You only need internet when adding new books (to look up details) and to receive updates. Everything else works without Wi-Fi.",
    faqId: "offline",
  },
  {
    q: "Can we install it on multiple computers?",
    a: "Each licence covers one installation for life. Bookburrow is designed to run on a single machine per library. If your school has two separate libraries, you'd need two licences.",
    faqId: "one-licence-many-computers",
  },
  {
    q: "Can we migrate from our current library management system?",
    a: "Yes. Bookburrow's desktop app provides a spreadsheet template you can fill with your existing book and borrower data, then import it directly. We also offer free migration support during your trial to help you get everything across.",
    faqId: "import-borrowers",
  },
];

interface PriceShape {
  amount: number;
  currency: string;
}

interface PriceResponse {
  initial?: PriceShape | null;
  renewal?: PriceShape | null;
  amount?: number;
  currency?: string;
}

export function Pricing() {
  const [initialPrice, setInitialPrice] = useState<number | null>(null);
  const [renewalPrice, setRenewalPrice] = useState<number | null>(null);
  const cardView = useInView<HTMLElement>(0.1);
  const faqView = useInView<HTMLElement>(0.05);

  useEffect(() => {
    supabase.functions.invoke<PriceResponse>("get-price").then(({ data }) => {
      if (!data) return;
      const initialAmount = data.initial?.amount ?? data.amount;
      const renewalAmount = data.renewal?.amount;
      if (typeof initialAmount === "number") setInitialPrice(initialAmount);
      if (typeof renewalAmount === "number") setRenewalPrice(renewalAmount);
    });
  }, []);

  return (
    <div className="editorial-page">
      {/* ── Hero ──────────────────────────────── */}
      <section className="page-hero grain relative bg-cream-50 pb-8 pt-16 md:pt-20">
        <div className="relative z-10 mx-auto max-w-7xl px-6 text-center">
          <span className="text-sm font-semibold uppercase tracking-widest text-forest-500">
            Pricing
          </span>
          <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight text-forest-950 sm:text-5xl">
            Pay once, stay up to date for a little.
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-bark-600">
            Bookburrow is &pound;149 once, with 12 months of updates and
            support included. After that, renew for &pound;49 a year &mdash;
            or don&rsquo;t. The software keeps working either way.
          </p>
        </div>
      </section>

      {/* ── Pricing cards ───────────────────────── */}
      <section
        ref={cardView.ref}
        className="bg-cream-50 py-16 md:py-24"
      >
        <div className="mx-auto grid max-w-4xl gap-8 px-6 md:grid-cols-2">
          {/* Initial licence */}
          <div
            className={`paper-card reveal rounded-3xl border-2 border-gold-400 bg-white p-8 shadow-xl shadow-forest-950/5 md:p-10 ${cardView.isVisible ? "visible" : ""}`}
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-gold-100 px-3 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-gold-500" />
              <span className="text-xs font-semibold text-gold-700">
                Bookburrow licence
              </span>
            </div>

            <div className="mt-6 flex items-baseline gap-2">
              {initialPrice === null ? (
                <span className="h-12 w-24 animate-pulse rounded-lg bg-cream-200" />
              ) : (
                <span className="font-display text-5xl font-bold text-forest-950">
                  &pound;{initialPrice}
                </span>
              )}
              <span className="text-lg text-bark-600">once</span>
            </div>
            <p className="mt-2 text-sm text-bark-600/70">
              Per school. VAT included. Yours to keep.
            </p>

            <div className="my-6 h-px bg-cream-200" />

            <ul className="space-y-3">
              {initialIncluded.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-forest-500" />
                  <span className="text-sm text-bark-700">{item}</span>
                </li>
              ))}
            </ul>

            <a
              href="/#waitlist"
              className="primary-action mt-8 block w-full rounded-xl bg-gold-500 px-6 py-4 text-center text-base font-bold text-bark-900 shadow-lg shadow-gold-500/20 transition-all hover:bg-gold-400 hover:shadow-xl"
            >
              Join the waitlist
            </a>
            <p className="mt-3 text-center text-xs text-bark-600/60">
              Currently invite-only &mdash; private beta.
            </p>
          </div>

          {/* Renewal */}
          <div
            className={`paper-card reveal rounded-3xl border border-cream-300/60 bg-white p-8 shadow-xl shadow-forest-950/5 md:p-10 ${cardView.isVisible ? "visible" : ""}`}
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-forest-100 px-3 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-forest-600" />
              <span className="text-xs font-semibold text-forest-700">
                Annual support renewal
              </span>
            </div>

            <div className="mt-6 flex items-baseline gap-2">
              {renewalPrice === null ? (
                <span className="h-12 w-20 animate-pulse rounded-lg bg-cream-200" />
              ) : (
                <span className="font-display text-5xl font-bold text-forest-950">
                  &pound;{renewalPrice}
                </span>
              )}
              <span className="text-lg text-bark-600">/ year</span>
            </div>
            <p className="mt-2 text-sm text-bark-600/70">
              Optional. Renew when you&rsquo;re ready; skip years freely.
            </p>

            <div className="my-6 h-px bg-cream-200" />

            <ul className="space-y-3">
              {renewalIncluded.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-forest-500" />
                  <span className="text-sm text-bark-700">{item}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/portal/dashboard"
              className="mt-8 block w-full rounded-xl border border-forest-700 px-6 py-4 text-center text-base font-semibold text-forest-800 transition-all hover:bg-forest-50"
            >
              Renew from your dashboard
            </Link>
            <p className="mt-3 text-center text-xs text-bark-600/60">
              Existing customers renew inside the portal.
            </p>
          </div>
        </div>
      </section>

      {/* ── Volume note ──────────────────────── */}
      <section className="bg-cream-100 py-16 md:py-20">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <h2 className="font-display text-2xl font-semibold text-forest-950">
            Multi-academy trusts
          </h2>
          <p className="mt-3 text-bark-600">
            Running Bookburrow across multiple schools? Get in touch for volume
            pricing. We offer discounts from 3+ licences and can invoice centrally.
          </p>
          <a
            href="mailto:support@bookburrow.uk"
            className="mt-6 inline-block text-sm font-semibold text-forest-600 underline decoration-forest-300 underline-offset-4 transition-colors hover:text-forest-800"
          >
            Contact us for a quote
          </a>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────── */}
      <section
        ref={faqView.ref}
        className="bg-cream-50 py-24 md:py-32"
      >
        <div className="mx-auto max-w-3xl px-6">
          <h2 className={`font-display text-3xl font-semibold tracking-tight text-forest-950 reveal ${faqView.isVisible ? "visible" : ""}`}>
            Frequently asked questions
          </h2>

          <div className="stagger mt-12 divide-y divide-cream-300/60">
            {faqs.map((faq) => (
              <div
                key={faq.q}
                className={`reveal py-7 first:pt-0 last:pb-0 ${faqView.isVisible ? "visible" : ""}`}
              >
                <h3 className="font-display text-base font-semibold text-forest-900">
                  {faq.q}
                </h3>
                <p className="mt-2 leading-relaxed text-bark-600">
                  {faq.a}
                </p>
                {faq.faqId && (
                  <Link
                    to={`/help/faq#${faq.faqId}`}
                    className="mt-3 inline-block text-sm font-semibold text-forest-700 underline decoration-gold-400 decoration-2 underline-offset-4 transition-colors hover:text-forest-800"
                  >
                    Full answer in the help centre &rarr;
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <Link
              to="/help/faq"
              className="inline-flex items-center gap-2 rounded-full border border-forest-700 px-6 py-3 font-semibold text-forest-700 transition-colors hover:bg-forest-700 hover:text-cream-50"
            >
              Browse the full FAQ &rarr;
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
